/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userService } from "@src/@api-services/user";
import type { TableSortValue } from "@src/shared/constants/sort-order.constants";

interface User {
  id: string | number;
  name: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  roleId: number;
  email: string;
  phone?: string;
  role?: string;
  status?: string;
}

interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: TableSortValue;
  [key: string]: any;
}

interface UsersState {
  allData: User[];
  total: number;
  params: GetUsersParams;
  selectedUser: User | null;
  loading: boolean;
  error: string | null;
  dropdownUsers: any[];
}

export const getAllUsers = createAsyncThunk(
  "users/getAllUsers",
  async (query: GetUsersParams = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getAllUsers(query);
      const payload = response?.data?.data ?? {};
      return {
        params: query,
        data: payload.data ?? [],
        total: payload.totalRecord ?? payload.totalRecords ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all users"
      );
    }
  }
);

export const getUser = createAsyncThunk(
  "users/getUser",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await userService.getUser(id);
      return response.data.data ?? response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const addUser = createAsyncThunk(
  "users/createUser",
  async (
    userData: Partial<any>,
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await userService.createUser(userData);
      const state = getState();
      await dispatch(getAllUsers(state.users.params));
      return response.data?.message || "User created successfully.";
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create user"
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (
    { id, userData }: { id: string | number; userData: Partial<User> },
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await userService.updateUser(id, userData);
      const state = getState();
      await dispatch(getAllUsers(state.users.params));
      return response.data?.message || "User updated successfully.";
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id: string | number, { dispatch, getState, rejectWithValue }: any) => {
    try {
      const response = await userService.deleteUser(id);
      const state = getState();
      await dispatch(getAllUsers(state.users.params));
      return response.data?.message || "User deleted successfully.";
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const getUserDropdown = createAsyncThunk(
  "users/dropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getUserDropDown();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users dropdown"
      );
    }
  }
);

const initialState: UsersState = {
  allData: [],
  total: 0,
  params: {},
  selectedUser: null,
  loading: false,
  error: null,
  dropdownUsers: [],
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.allData = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.allData = [];
        state.total = 0;
      })
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(getUser.rejected, (state) => {
        state.loading = false;
        state.selectedUser = null;
      })
      .addCase(getUserDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserDropdown.fulfilled, (state, action) => {
        state.loading = false;
        state.dropdownUsers = action.payload?.data || action.payload || [];
      })
      .addCase(getUserDropdown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.dropdownUsers = [];
      })
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          ["createUser", "updateUser", "deleteUser"].some((type) =>
            action.type.includes(type)
          ),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/fulfilled") &&
          ["createUser", "updateUser", "deleteUser"].some((type) =>
            action.type.includes(type)
          ),
        (state) => {
          state.loading = false;
        }
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          ["createUser", "updateUser", "deleteUser"].some((type) =>
            action.type.includes(type)
          ),
        (state, action: any) => {
          state.loading = false;
          state.error = action?.payload as string;
        }
      );
  },
});

export const { clearSelectedUser } = usersSlice.actions;

export default usersSlice.reducer;
